import { useEffect, useState } from "react";
import { api, orderPdfUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { MessageCircle, FileText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import NegotiationPanel from "@/components/shared/NegotiationPanel";
import MessageThread from "@/components/shared/MessageThread";

const statusColor = {
    pending: "bg-yellow-100 text-yellow-900 border-yellow-400",
    negotiating: "bg-orange-50 text-orange-900 border-orange-400",
    agreed: "bg-purple-50 text-purple-900 border-purple-400",
    confirmed: "bg-blue-50 text-blue-900 border-blue-400",
    in_production: "bg-neutral-100 text-neutral-900 border-neutral-400",
    shipped: "bg-green-50 text-green-900 border-green-500",
    cancelled: "bg-red-50 text-red-900 border-red-400",
};

export default function MyOrders() {
    const [orders, setOrders] = useState([]);
    const [openId, setOpenId] = useState(null);
    const { user } = useAuth();

    const load = () => api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const shareWhatsApp = (o) => {
        const cur = (c) => (c === "GBP" ? "£" : c === "USD" ? "$" : c === "EUR" ? "€" : "");
        const totalPcs = o.items.reduce((s, i) => s + i.quantity, 0);
        const lines = [
            `*Order ${o.order_number}*`,
            `Client: ${user?.company || user?.name} (ID ${user?.login_id})`,
            "",
            ...o.items.map((it) => `• ${it.title}${it.sku_code ? ` (${it.sku_code})` : ""} × ${it.quantity} pcs @ ${cur(it.currency)}${it.price.toFixed(2)}/pc = ${cur(it.currency)}${(it.price * it.quantity).toFixed(2)}`),
            "",
            `*Total pieces: ${totalPcs.toLocaleString()}*`,
            `*Total value: ${cur(o.currency)}${o.total.toFixed(2)}*`,
            `Status: ${o.status}`,
        ];
        window.open(`https://wa.me/?text=${encodeURIComponent(lines.join("\n"))}`, "_blank");
    };

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-10 py-6 sm:py-10">
            <div className="mb-8">
                <div className="overline text-neutral-500">History</div>
                <h1 className="font-display text-5xl tracking-tight leading-none mt-2">My Orders</h1>
            </div>

            {orders.length === 0 && (
                <div className="border border-dashed border-border p-12 text-center">
                    <div className="overline text-neutral-500">Empty</div>
                    <div className="font-display text-2xl mt-2">No orders yet</div>
                    <div className="text-sm text-neutral-500 mt-2 font-body">Head to the catalog to place your first order.</div>
                </div>
            )}

            <div className="space-y-3">
                {orders.map((o) => (
                    <div key={o.id} className="border border-border" data-testid={`my-order-${o.id}`}>
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 p-5 items-center">
                            <div className="md:col-span-2">
                                <div className="font-mono text-xs text-neutral-500">{o.order_number}</div>
                                <div className="font-body text-xs text-neutral-500 mt-1">{new Date(o.created_at).toLocaleString()}</div>
                            </div>
                            <div>
                                <div className="overline text-neutral-500">Pieces</div>
                                <div className="font-body text-sm mt-1 tabular-nums">{o.items.reduce((s,i)=>s+i.quantity,0).toLocaleString()}</div>
                            </div>
                            <div>
                                <div className="overline text-neutral-500">Total</div>
                                <div className="font-display text-2xl tabular-nums leading-none mt-1">£{o.total.toFixed(2)}</div>
                            </div>
                            <div className="flex flex-col gap-2">
                                <span className={`inline-block px-2 py-1 border uppercase tracking-widest text-[10px] font-semibold text-center ${statusColor[o.status] || ""}`}>
                                    {o.status.replace("_", " ")}
                                </span>
                                <button onClick={() => setOpenId(openId === o.id ? null : o.id)} className="text-xs uppercase tracking-widest text-neutral-600 hover:text-black" data-testid={`toggle-my-order-${o.id}`}>
                                    {openId === o.id ? "Hide" : "Details"}
                                </button>
                            </div>
                        </div>
                        {openId === o.id && (
                            <div className="border-t border-border bg-neutral-50 p-5 space-y-4">
                                <NegotiationPanel order={o} role="client" onChanged={load}/>
                                <MessageThread threadType="order" threadId={o.id}/>
                                <div className="pt-2 flex flex-wrap gap-2">
                                    {["confirmed", "in_production", "shipped"].includes(o.status) && (
                                        <a
                                            href={orderPdfUrl(o.id)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-2 border border-black bg-white text-black px-4 h-10 uppercase tracking-widest text-[10px] font-semibold hover:bg-black hover:text-white transition-colors"
                                            data-testid={`client-order-pdf-${o.id}`}
                                        >
                                            <FileText className="h-3.5 w-3.5" strokeWidth={1.5}/> Download Confirmation PDF
                                        </a>
                                    )}
                                    <Button
                                        onClick={() => shareWhatsApp(o)}
                                        className="rounded-none bg-[#25D366] text-white hover:bg-[#128C7E] uppercase tracking-widest text-xs font-semibold"
                                        data-testid={`share-order-${o.id}`}
                                    >
                                        <MessageCircle className="h-4 w-4 mr-2" strokeWidth={1.5}/> Share on WhatsApp
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
